import { fetcher, useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
  Text,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { CancelCheckModal } from "../../../components/modals/CancelCheckModal";
import { download } from "../../group-overview/utils";

interface caseDetailActionsDropdownProps {
  checks: readonly components["schemas"]["CaseChecksContent"][];
}

export function CaseDetailActionsDropdown(
  props: caseDetailActionsDropdownProps
) {
  const { checks } = props;
  const { t } = useTranslation();
  const { caseId } = useParams();
  const { data, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/`
  );

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const ukRtwData = data?.certificates?.presigned_urls?.UK_RIGHT_TO_WORK_1;
  const ukCrimCheck = checks?.find(
    (item) =>
      ["BASIC_UK_DBS_1", "BASIC_UK_DS_1"].includes(item.type) &&
      !["CANCELLED", "COMPLETED"].includes(item.status)
  );

  const shouldDisplayDropdown = [ukRtwData, ukCrimCheck].some((item) => item);

  if (!shouldDisplayDropdown) {
    return null;
  }

  const handleDownloadRtw = () => {
    if (ukRtwData) {
      try {
        fetcher.submit(
          `/api/internal/client-portal/case/${ukRtwData.id}/accessed/`
        );
        download(ukRtwData.url, "uk_rtw_certificate");
      } catch (e) {
        console.error("Failed to log access or download certificate.", e);
      }
    }
  };

  const handleOpenCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  return (
    <>
      <DropdownMenu
        triggerButton={
          <GhostIconButton
            testID="case-details-item-expand-button"
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        onItemAction={(menuItem) => {
          switch (menuItem) {
            case "download-uk-rtw-certificate":
              handleDownloadRtw();
              break;
            case "cancel-uk-crim-check":
              handleOpenCancelModal();
              break;
          }
        }}
        placement="bottom right"
      >
        <MenuSection>
          {ukRtwData ? (
            <MenuItem
              id="download-uk-rtw-certificate"
              testID="case-details-download-uk-rtw-certificate-button"
            >
              <Text style="input/text-field">{t("View RTW Certificate")}</Text>
            </MenuItem>
          ) : (
            <></>
          )}
          {ukCrimCheck ? (
            <MenuItem
              id="cancel-uk-crim-check"
              testID="case-details-cancel-uk-crim-check-button"
            >
              <Text style="input/text-field">
                {t("Cancel UK Criminal Check")}
              </Text>
            </MenuItem>
          ) : (
            <></>
          )}
        </MenuSection>
      </DropdownMenu>

      <CancelCheckModal
        isOpen={isCancelModalOpen && Boolean(ukCrimCheck)}
        onClose={() => setIsCancelModalOpen(false)}
        checkId={ukCrimCheck?.id}
        caseId={caseId}
        mutate={mutate}
      />
    </>
  );
}
